import React, { Component } from "react";
import PropTypes from 'prop-types';
import MDReactComponent from 'markdown-react-js';
import Katex from './Katex';
import MkImgSize from 'markdown-it-imsize';
import ApiService from "../../../Services/ApiService";
import Frame from "../../../PageLayout/Frame/Frame";

class Markdown extends Component {
  static propTypes = {
    children: PropTypes.string,
    projectId: PropTypes.any,
    path: PropTypes.string,
    maxSize: PropTypes.number,
  };

  static defaultProps = {
    projectId: null,
  };

  constructor() {
    super();

    this.katexKey = 0;
    this.fileProxyStack = [];
  }

  scanDelims = (state, start) => {
    var pos = start, lastChar, nextChar, count,
      isLastWhiteSpace, isLastPunctChar,
      isNextWhiteSpace, isNextPunctChar,
      can_open = true,
      can_close = true,
      max = state.posMax,
      marker = state.src.charCodeAt(start),
      isWhiteSpace = state.md.utils.isWhiteSpace,
      isPunctChar = state.md.utils.isPunctChar,
      isMdAsciiPunct = state.md.utils.isMdAsciiPunct;

    //  treat  beginning  of  the  line  as  a  whitespace
    lastChar = start > 0 ? state.src.charCodeAt(start - 1) : 0x20;

    while (pos < max && state.src.charCodeAt(pos) === marker) { pos++; }

    if (pos >= max) {
      can_open = false;
    }

    count = pos - start;

    nextChar = pos < max ? state.src.charCodeAt(pos) : 0x20;

    isLastPunctChar = isMdAsciiPunct(lastChar) || isPunctChar(String.fromCharCode(lastChar));
    isNextPunctChar = isMdAsciiPunct(nextChar) || isPunctChar(String.fromCharCode(nextChar));
    isLastWhiteSpace = isWhiteSpace(lastChar);
    isNextWhiteSpace = isWhiteSpace(nextChar);

    if (isNextWhiteSpace) {
      can_open = false;
    } else if (isNextPunctChar) {
      if (!(isLastWhiteSpace || isLastPunctChar)) {
        can_open = false;
      }
    }

    if (isLastWhiteSpace) {
      can_close = false;
    } else if (isLastPunctChar) {
      if (!(isNextWhiteSpace || isNextPunctChar)) {
        can_close = false;
      }
    }

    return {
      can_open: can_open,
      can_close: can_close,
      delims: count
    };
  }


  mark(state, silent) {
    var startCount,
      count,
      tagCount,
      found,
      stack,
      res,
      token,
      max = state.posMax,
      start = state.pos,
      marker = state.src.charCodeAt(start);

    if (marker !== this.separator) { return false; }
    if (silent) { return false; }  //  don't  run  any  pairs  in  validation  mode

    res = this.scanDelims(state, start);
    startCount = res.delims;

    if (!res.can_open) {
      state.pos += startCount;
      //  Earlier  we  checked  !silent,  but  this  implementation  does  not  need  it
      state.pending += state.src.slice(start, state.pos);
      return true;
    }

    stack = Math.floor(startCount / 2);
    if (stack <= 0) { return false; }
    state.pos = start + startCount;

    while (state.pos < max) {
      if (state.src.charCodeAt(state.pos) === marker) {
        res = this.scanDelims(state, state.pos);
        count = res.delims;
        tagCount = Math.floor(count / 2);
        if (res.can_close) {
          if (tagCount >= stack) {
            state.pos += count - 2;
            found = true;
            break;
          }
          stack -= tagCount;
          state.pos += count;
          continue;
        }

        if (res.can_open) { stack += tagCount; }
        state.pos += count;
        continue;
      }

      state.md.inline.skipToken(state);
    }

    if (!found) {
      //  parser  failed  to  find  ending  tag,  so  it's  not  valid  emphasis
      state.pos = start;
      return false;
    }

    //  found!
    state.posMax = state.pos;
    state.pos = start + 2;

    //  Earlier  we  checked  !silent,  but  this  implementation  does  not  need  it
    token = state.push(this.mark + '_open', this.mark, 1);
    token.markup = String.fromCharCode(marker) + String.fromCharCode(marker);

    state.md.inline.tokenize(state);

    token = state.push(this.mark + '_close', this.mark, -1);
    token.markup = String.fromCharCode(marker) + String.fromCharCode(marker);

    state.pos = state.posMax + 2;
    state.posMax = max;
    return true;
  }

  plugins = (md) => {
    md.use(MkImgSize);
    md.inline.ruler.before('emphasis', 'katex', this.mark.bind({ ...this, mark: 'katex', separator: 0x24 }));
  }

  katexSyntaxPreprocesor = (textMark) => {
    function applyInBetween(text, separator, apply) {
      let prev = '',
        next = text,
        start, end,
        escape = [',', '`', '"'];

      while (next.length) {
        if ((start = next.indexOf(separator)) != -1 && (end = next.substr(start + separator.length).indexOf(separator)) != -1) {
          if ((start > 0 && escape.indexOf(next[start - 1]) != -1) ||
              ((start + separator.length) < next.length && escape.indexOf(next[start + separator.length]) != -1) ||
              end === 0) {
            const steping = separator.length * (end === 0 ? 2 : 1);
            prev += next.substr(0, start + steping);
            next = next.substr(start + steping);

            continue;
          }
          const newMark = apply(next.substr(start + separator.length, end));
          prev += next.substr(0, start) + newMark;
          next = next.substr(start + separator.length * 2 + end);

        } else {
          prev += next;
          break;
        }
      }
      return prev;
    }

    textMark = applyInBetween(textMark, '$$', (t) => '$$block:' + t.trim() + '$$');
    textMark = applyInBetween(textMark, '$', (t) => '$$' + t.trim() + '$$');
    return textMark;
  }

  getDevOpsFile = (tag, props, src) => {
    let path = src;

    path = path.trim();
    if (!path.length) {
      return;
    }

    if (path[0] === '$') {
      path = path.substr(1);
    }

    // handle markdown relative path
    if (this.props.path && path[0] != '/') {
      path = this.props.path.substr(0, this.props.path.lastIndexOf('/')) + '/' + path;
    }

    // stack duplicate img call together
    let fileInstance;
    if ((fileInstance = this.fileProxyStack.find(a => a.path === path))) {
      let newRequesterIndex = fileInstance.ref.length;
      fileInstance.ref.push(null);
      props.ref = (ref) => {
        if (!ref) {
          return ;
        }
        fileInstance.ref[newRequesterIndex] = ref;
        this.installDevOpsFile(fileInstance, newRequesterIndex);
      };
      return ;
    }

    fileInstance = {
      path: path,
      type: tag,
      ref: [null],
    };

    props.ref = (ref) => {
      if (!ref) {
        return ;
      }
      fileInstance.ref[0] = ref;
      this.requestDevOpsFiles(fileInstance);
      this.installDevOpsFile(fileInstance, 0);
    };

    this.fileProxyStack.push(fileInstance);
  }

  requestDevOpsFiles = (file) => {
    file.request = ApiService.getProjectRepositoryFile(this.props.projectId, file.path);
  }

  installDevOpsFile = (file, requesterId) => {
    const url = window.URL || window.webkitURL;

    if (!file.request) {
      return ;
    }

    let checkAllDone = (dest) => {
      if (!file.ref.find(r => r != null)) {
        file.request = null;
        this.fileProxyStack.splice(this.fileProxyStack.indexOf(file), 1);
        if (dest) {
          dest.onload = function() {
            url.revokeObjectURL(file.imgSrc);
          };
        }
      }
    };

    file.request
      .then((responseBlob) => {
        if (!file.imgSrc) {
          file.imgSrc = url.createObjectURL(responseBlob);
        }

        let dest = file.ref[requesterId];


        dest.src = file.imgSrc;
        file.ref[requesterId] = null;
        checkAllDone(dest);
      })
      .catch((error) => {
        clearTimeout(error.errorDisplayTimer);
        file.ref[requesterId] = null;
        checkAllDone();
      });
  }

  handleIterate = (Tag, props, children) => {
    if (Tag === 'katex') {
      if (children[0].indexOf('block:') != -1) {
        let t = [ ...children ];
        t[0] = t[0].substr(6);
        return <Katex style={{ 'display': 'block', 'textAlign': 'center' }} key={++this.katexKey}>{t}</Katex>;
      } else {
        return <Katex key={++this.katexKey}>{children}</Katex>;
      }
    }
    if  (Tag === 'img')  {
      if (this.props.projectId != null && props.src) {
        if (props.src.indexOf('://') == -1) {
          //this.getDevOpsFile(Tag, props, props.src);
          delete props.src;
        }
      }
    }

    return <Tag {...props}>{children}</Tag>;
  }

  render = () => {
    return (
      <Frame maxSize={this.props.maxSize} className='markdown'>
        <div className='codai-markdown'>
          <MDReactComponent
            text={this.katexSyntaxPreprocesor(this.props.children)}
            plugins={[this.plugins]}
            onIterate={this.handleIterate}
          />
        </div>
      </Frame>
    );
  };
}

export default Markdown;
